import {
  AlertLooks,
  ButtonLooks,
  CircleLoader,
  FontIcons,
  FontTransforms,
  FontWeights,
  HeadingLevels,
  StandardAlert,
  StandardButton,
  StandardDivider,
  StandardFieldset,
  StandardHeading,
  StandardText,
  StandardTextarea,
  StandardTextfield
} from '@brandfolder/react';
import { localizeDate } from '@brandfolder/utilities';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { object, SchemaOf, string } from 'yup';

import { UserGroupClient, UserGroupServerUpdate } from '@api/v4/user_groups/UserGroupsTypes';
import { useFetch, UseFetchOptions } from '@api/ApiHelper';
import { ApiDatum, ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { User } from '@api/v4/UserTypes';
import { mapUserGroupServerToClient, UserGroupServer } from '@api/v4/user_groups';
import { StandardDialog } from '@components/library/dialog';
import { getUserDisplayName } from '@helpers/user';
import { UserGroups404 } from './UserGroups404';

import classes from './styles/user-groups.module.scss';

const schema: SchemaOf<UserGroupServerUpdate> = object().shape({
  description: string().max(2000, bfTranslate('Maximum characters allowed is 2000')),
  name: string().required(bfTranslate('Group name is required'))
});

export const UserGroupsEdit: FunctionComponent = () => {
  const params = useParams();
  const { userGroupKey } = params;

  const {
    error: errorGet,
    loading: loadingGet,
    response: responseGet
  } = useFetch<ApiDatum<UserGroupServer, 'user_groups', User, 'users'>>({
    loadingDelay: 50,
    url: `/api/v4/${BFG.resource.type}s/${BFG.resource.key}/organization/user_groups/${userGroupKey}?include=creator`
  });

  const fetchOptions: UseFetchOptions = {
    fetchOnMount: false,
    method: 'PUT',
    url: `/api/v4/user_groups/${userGroupKey}`
  };

  const {
    error: errorUpdate,
    fetch: fetchUpdate,
    loading: loadingUpdate,
    response: responseUpdate
  } = useFetch<ApiDatumResponse<UserGroupServer, 'user_groups'>>(fetchOptions);

  const [showUpdate, setShowUpdate] = useState(false);
  const [userGroup, setUserGroup] = useState<UserGroupClient | null>(null);
  const [user, setUser] = useState<{ email: string; fullName: string; } | null>(null);

  const { formState, handleSubmit, register, reset } = useForm<UserGroupServerUpdate>({
    resolver: yupResolver(schema)
  });

  const { errors } = formState;

  useEffect(() => {
    if (responseGet?.data) {
      setUserGroup(mapUserGroupServerToClient(responseGet.data.attributes));
      setUser({
        email: responseGet.included[0].attributes.email,
        fullName: getUserDisplayName(responseGet.included[0].attributes)
      });
    }
  }, [responseGet]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (responseUpdate?.data) {
      setUserGroup(mapUserGroupServerToClient(responseUpdate.data.attributes));
      setShowUpdate(false);
    }
  }, [responseUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userGroup) {
      reset({
        description: userGroup.description,
        name: userGroup.name
      });
    }
  }, [userGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingGet) return <CircleLoader label={bfTranslate('Loading')} />;

  if (errorGet && !loadingGet && !userGroup) {
    return <UserGroups404 />;
  }

  const { createdAt, description, memberCount, name } = userGroup;
  const { email, fullName } = user;

  return (
    <div className={classes.userGroups}>
      <section>
        <StandardText className={classes.return}>
          <Link className={classes.link} to={`/${BFG.resource.slug}/bulk_management/user_groups`}>
            {bfTranslate('Return to User Groups')}
          </Link>
        </StandardText>
      </section>
      <section>
        {!errorUpdate && !loadingUpdate && responseUpdate?.data && (
          <StandardAlert
            className={classes.alert}
            look={AlertLooks.Success}
          >
            {bfTranslate('User group updated successfully!')}
          </StandardAlert>
        )}
        <StandardDialog
          id="update-user-group-dialog"
          onClose={(): void => {
            setShowUpdate(false);
          }}
          open={showUpdate}
          setOpen={(): void => {
            setShowUpdate(false);
          }}
          showFooter={false}
          title={bfTranslate('Edit group')}
          titleIcon={`bff-${FontIcons.Edit}`}
          width={450}
        >
           <>
            {loadingUpdate ? (
              <>
                <CircleLoader label={bfTranslate('Loading')} />
              </>
            ) : (
              <>
                {errorUpdate && (
                  <StandardAlert
                    className={classes.alert}
                    look={AlertLooks.Error}
                  >
                    {bfTranslate('Something went wrong while updating the user group. Please try again or contact customer support.')}
                  </StandardAlert>
                )}

                <form
                  id="user-groups-update-form"
                  noValidate
                  onSubmit={handleSubmit((values) => {
                    fetchUpdate({
                      ...fetchOptions,
                      body: {
                        data: {
                          attributes: values
                        }
                      }
                    });
                  })}
                >
                  <StandardFieldset
                    className={classes.updateFieldset}
                    legend={bfTranslate('Update user group')}
                    showLegend={false}
                  >
                    <StandardTextfield
                      {...register('name')}
                      className={classes.updateTextfield}
                      error={errors.name?.message}
                      id="name"
                      label={bfTranslate('Group name')}
                      required
                      requiredLabel={bfTranslate('Required')}
                    />
                    <StandardTextarea
                      {...register('description')}
                      className={classes.updateTextfield}
                      error={errors.description?.message}
                      id="description"
                      label={
                        <>
                          {bfTranslate('Description')}&nbsp;
                          <small className={classes.small}>
                            ({bfTranslate('optional')})
                          </small>
                        </>
                      }
                      maxLength={2000}
                    />
                    <StandardButton
                      className={classes.updateButton}
                      id="update-user-group"
                      type="submit"
                    >
                      {bfTranslate('Update group')}
                    </StandardButton>
                  </StandardFieldset>
                </form>
              </>
            )}
          </>
        </StandardDialog>
        <>
          <StandardHeading
            className={classes.name}
            level={HeadingLevels.h2}
            weight={FontWeights.Book}
          >
            {name}
          </StandardHeading>
          {description && (
            <StandardText className={classes.description}>
              {description}
            </StandardText>
          )}
          <StandardButton
            className={classes.edit}
            id="edit-user-group"
            look={ButtonLooks.Secondary}
            onClick={(): void => {
              setShowUpdate(true);
            }}
            startIcon={FontIcons.Edit}
          >
            {bfTranslate('Edit user group')}
          </StandardButton>
        </>
        <StandardDivider />
        <div className={classes.details}>
          <div className={classes.detail}>
            <StandardHeading
              level={HeadingLevels.h6}
              transform={FontTransforms.Uppercase}
            >
              {bfTranslate('Created By')}
            </StandardHeading>
            <StandardText>
              {fullName || email}
            </StandardText>
          </div>
          <div className={classes.detail}>
            <StandardHeading
              level={HeadingLevels.h6}
              transform={FontTransforms.Uppercase}
            >
              {bfTranslate('Created On')}
            </StandardHeading>
            <StandardText>
              {localizeDate(createdAt)}
            </StandardText>
          </div>
          <div className={classes.detail}>
            <StandardHeading
              level={HeadingLevels.h6}
              transform={FontTransforms.Uppercase}
            >
              {bfTranslate('Number of members')}
            </StandardHeading>
            <StandardText>
              {memberCount}
            </StandardText>
          </div>
        </div>
      </section>
    </div>
  );
};
